import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import Loading from "../ui/Loading";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import SEO from "../../seo/SEO";
import { buildSectionSeoTitle } from "../../seo/seoText";
import {
  extractNewsSlugFromRouteParam,
  fetchCmsNewsBySlug,
  fetchLatestCmsNews,
  getCmsMediaCandidateUrls,
  getCmsMediaUrl,
  normalizeCmsPostForLegacyView,
  normalizeCmsPostsForLegacyView,
  resolveCmsHtmlAssetUrls,
} from "../../api/cmsClient";
import { getCmsLocaleFromLanguage, hasEnglishCmsVersion } from "../../api/cmsLocale";
import {
  cacheCmsLatestPosts,
  cacheCmsNewsDetail,
  createFallbackNotice,
  fetchLocalNews,
  getCachedCmsLatestPosts,
  getCachedCmsNewsDetail,
  isNotFoundError,
  shouldSuppressFallbackNotice,
} from "../../api/newsFallback";
import NewsFallbackNotice from "./NewsFallbackNotice";
import {
  mergeNewsPostWithLocalMediaFallback,
  mergeNewsPostsWithLocalMediaFallbacks,
} from "../../api/legacyMediaMerge";
import "./../../assets/css/slick.css";

const latestPostsLimit = 5;
const localFallbackImage = "/assets/img/logo/fpepblog.png";

function parseBlogDate(dateString) {
  if (!dateString) return null;

  const match = dateString.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  if (!match) return null;

  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
}

function createNewsDescription(post) {
  const raw = post?.seoDescription || post?.attributes?.Description || "";
  const normalized = raw.replace(/\s+/g, " ").trim();

  if (normalized.length <= 160) return normalized;
  return `${normalized.slice(0, 157).trim()}…`;
}

function slugify(value = "") {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "dj")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function getNewsSlug(post) {
  return post?.attributes?.Slug || slugify(post?.attributes?.Title || "novost");
}

function getNewsPath(post) {
  if (post?.routePath) return post.routePath;
  return `/novosti/${post.id}-${getNewsSlug(post)}`;
}

function extractNewsId(routeParam = "") {
  const match = String(routeParam || "").match(/^(\d+)/);
  return match ? match[1] : routeParam;
}

function getDescriptionParagraphs(description = "") {
  return String(description)
    .split(/\n\s*\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function findLocalPost(posts, routeParam = "") {
  const numericId = extractNewsId(routeParam);
  const routeSlug = extractNewsSlugFromRouteParam(routeParam);

  return posts.find((item) => {
    const itemId = item.id?.toString();
    const itemSlug = getNewsSlug(item);

    return itemId === numericId || itemSlug === routeSlug || `${itemId}-${itemSlug}` === routeParam;
  });
}

function normalizeImageCandidates(candidates = []) {
  return Array.isArray(candidates) ? candidates.filter(Boolean) : [candidates].filter(Boolean);
}

function ResilientImage({ candidates, fallbackSrc = "/assets/img/logo/fpepblog.png", alt, ...props }) {
  const normalizedCandidates = [...normalizeImageCandidates(candidates), fallbackSrc].filter(Boolean);
  const candidatesKey = normalizedCandidates.join("|");
  const [candidateIndex, setCandidateIndex] = React.useState(0);

  React.useEffect(() => {
    setCandidateIndex(0);
  }, [candidatesKey]);

  if (!normalizedCandidates.length) return null;

  return (
    <img
      {...props}
      src={normalizedCandidates[candidateIndex]}
      alt={alt}
      onError={(event) => {
        const nextIndex = candidateIndex + 1;
        if (nextIndex < normalizedCandidates.length) {
          event.currentTarget.src = normalizedCandidates[nextIndex];
          setCandidateIndex(nextIndex);
          return;
        }

        if (typeof props.onError === "function") {
          props.onError(event);
        }
      }}
    />
  );
}


function NewsGalleryArrow({ direction = "next", label, onClick }) {
  return (
    <button
      type="button"
      className={`fpep-news-gallery__arrow fpep-news-gallery__arrow--${direction}`}
      onClick={onClick}
      aria-label={label}
    >
      <i className={direction === "prev" ? "fas fa-long-arrow-alt-left" : "fas fa-long-arrow-alt-right"} />
    </button>
  );
}

function normalizeCmsPostForDetailView(cmsPost) {
  const normalized = normalizeCmsPostForLegacyView(cmsPost);

  if (!normalized) return null;

  const contentHtml = resolveCmsHtmlAssetUrls(cmsPost?.content?.html || "");
  const gallery = Array.isArray(cmsPost?.gallery) ? cmsPost.gallery : [];
  const coverImageCandidates = getCmsMediaCandidateUrls(cmsPost?.coverImage, ["medium", "thumb", "original"]);
  const coverImage = coverImageCandidates[0] || normalized.attributes.Image;
  const ogImage = getCmsMediaUrl(cmsPost?.ogImage, "medium") || coverImage;

  return {
    ...normalized,
    routePath: normalized.routePath || cmsPost.routePath,
    contentHtml,
    seoTitle: cmsPost?.seo?.title || cmsPost?.title || normalized.attributes.Title,
    seoDescription: cmsPost?.seo?.description || normalized.attributes.Description,
    ogImage,
    articleDatePublished: cmsPost?.publishedAt || null,
    articleDateModified: cmsPost?.updatedAt || cmsPost?.publishedAt || null,
    attributes: {
      ...normalized.attributes,
      Description:
        cmsPost?.excerpt ||
        cmsPost?.content?.textPreview ||
        normalized.attributes.Description ||
        "",
      Image: coverImage,
      ImageCandidates: coverImageCandidates.length ? coverImageCandidates : normalized.attributes.ImageCandidates,
      Gallery: {
        data: gallery
          .map((item) => getCmsMediaCandidateUrls(item, ["medium", "thumb", "original"]))
          .filter((candidates) => candidates.length),
      },
    },
  };
}

function NovostDetail() {
  const [t, i18n] = useTranslation();
  const cmsLocale = getCmsLocaleFromLanguage(i18n.language);
  const isEnglish = cmsLocale === "en";
  const { id: routeParam } = useParams();
  const location = useLocation();

  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [fallbackSource, setFallbackSource] = useState("cms");


  const sliderSettings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <NewsGalleryArrow direction="prev" label={isEnglish ? "Previous image" : "Prethodna slika"} />,
    nextArrow: <NewsGalleryArrow direction="next" label={isEnglish ? "Next image" : "Sljedeća slika"} />,
    customPaging: () => <span className="fpep-news-gallery__dot" />,
    appendDots: (dots) => (
      <div className="fpep-news-gallery__dots-wrap">
        <ul className="fpep-news-gallery__dots">{dots}</ul>
      </div>
    ),
  };

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadLocalFallback = async (reason = null) => {
      const sortedData = await fetchLocalNews();
      const found = findLocalPost(sortedData, routeParam);

      if (!isMounted) return;

      setPost(found || null);
      setRecentPosts(sortedData.slice(0, latestPostsLimit));
      setFallbackSource("local");
      setLoadError(reason);
    };

    const loadPost = async () => {
      setLoading(true);
      setLoadError(null);

      const cmsSlug = extractNewsSlugFromRouteParam(routeParam);

      try {
        const [cmsPostResult, latestResult, localPostsForMediaFallback] = await Promise.all([
          fetchCmsNewsBySlug(cmsSlug, { locale: cmsLocale, signal: controller.signal }),
          fetchLatestCmsNews({ limit: latestPostsLimit, locale: cmsLocale, signal: controller.signal }),
          fetchLocalNews().catch(() => []),
        ]);

        if (!isMounted) return;

        const normalizedPost = mergeNewsPostWithLocalMediaFallback(
          normalizeCmsPostForDetailView(cmsPostResult.data),
          localPostsForMediaFallback,
        );
        const normalizedRecent = mergeNewsPostsWithLocalMediaFallbacks(
          normalizeCmsPostsForLegacyView(latestResult.data || []),
          localPostsForMediaFallback,
        );

        setPost(normalizedPost);
        setRecentPosts(normalizedRecent);
        setFallbackSource("cms");
        setLoadError(null);

        if (normalizedPost) {
          cacheCmsNewsDetail(cmsSlug, normalizedPost, cmsLocale);
        }

        if (normalizedRecent.length) {
          cacheCmsLatestPosts(latestPostsLimit, normalizedRecent, cmsLocale);
        }
      } catch (error) {
        if (!isMounted || error?.name === "AbortError") return;

        console.warn("CMS news detail unavailable. Falling back to local news JSON.", error);

        if (!isNotFoundError(error)) {
          const cachedPost = getCachedCmsNewsDetail(cmsSlug, cmsLocale);

          if (cachedPost) {
            setPost(cachedPost);
            setRecentPosts(getCachedCmsLatestPosts(latestPostsLimit, cmsLocale));
            setFallbackSource("cache");
            setLoadError(createFallbackNotice({ source: "cache", context: "detail" }));
            return;
          }
        }

        const fallbackReason = shouldSuppressFallbackNotice(error)
          ? null
          : createFallbackNotice({ source: "local", context: "detail" });

        try {
          await loadLocalFallback(fallbackReason);
        } catch (fallbackError) {
          if (!isMounted) return;

          console.error("Error fetching local post:", fallbackError);
          setPost(null);
          setRecentPosts([]);
          setFallbackSource("local");
          setLoadError("Novost trenutno nije dostupna.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPost();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [routeParam, cmsLocale]);

  if (loading) return <Loading />;

  if (!post) {
    const notFoundTitle = isEnglish
      ? "News post not found | FPEP Bar"
      : "Novost nije pronađena | FPEP Bar";
    const notFoundDescription = isEnglish
      ? "The requested FPEP Bar news post is unavailable or no longer published."
      : "Tražena novost FPEP Bar nije dostupna ili više nije objavljena.";

    return (
      <>
        <SEO
          title={notFoundTitle}
          description={notFoundDescription}
          pathname={location.pathname}
          image="/assets/img/social/fpep-news-gallery.webp"
          imageAlt={notFoundTitle}
          imageType="image/webp"
          imageWidth={1200}
          imageHeight={630}
          noindex
          disableLanguageAlternates
        />
        <section className="post-detail pt-120 pb-120">
          <div className="container">
            <div className="alert alert-warning" role="alert">
              {loadError || (isEnglish ? "Post was not found." : "Objava nije pronađena.")}
            </div>
            <Link to="/novosti" className="btn ss-btn smoth-scroll">
              {isEnglish ? "Back to news" : "Nazad na novosti"}
            </Link>
          </div>
        </section>
      </>
    );
  }

  const canonicalPath = getNewsPath(post);

  if (location.pathname !== canonicalPath) {
    return <Navigate to={canonicalPath} replace />;
  }

  const articleDate = post.articleDatePublished
    ? post.articleDatePublished.slice(0, 10)
    : parseBlogDate(post.attributes.Date);
  const articleDateModified = post.articleDateModified
    ? post.articleDateModified.slice(0, 10)
    : articleDate;
  const pageTitle = buildSectionSeoTitle(
    post.seoTitle || post.attributes.Title,
    isEnglish ? "News" : "Novosti",
  );
  const pageDescription = createNewsDescription(post);
  const pageImage = post.ogImage || post.attributes.Image || "/assets/img/social/fpep-news-gallery.webp";
  const hasEnglishVersion = hasEnglishCmsVersion(post);
  const isEnglishFallback = isEnglish && !hasEnglishVersion;
  const paragraphs = getDescriptionParagraphs(post.attributes.Description);
  const hasCmsContentHtml = Boolean(post.contentHtml);

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        pathname={canonicalPath}
        pageType="Article"
        image={pageImage}
        imageAlt={post.attributes.Title}
        section={isEnglish ? "News" : "Novosti"}
        noindex={isEnglishFallback}
        canonicalLocale={isEnglishFallback ? "me" : undefined}
        englishAlternateAvailable={hasEnglishVersion}
        breadcrumbs={[
          { name: isEnglish ? "Home" : "Početna", path: "/" },
          { name: isEnglish ? "News" : "Novosti", path: "/novosti" },
          { name: post.attributes.Title, path: canonicalPath },
        ]}
        article={{
          headline: post.attributes.Title,
          datePublished: articleDate,
          dateModified: articleDateModified,
          image: pageImage,
        }}
      />

      <section className="post-detail pt-120 pb-120">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <article className="fpep-news-detail-article">
                <Link to="/novosti" className="fpep-news-detail-back">
                  <i className="fas fa-long-arrow-alt-left" /> {isEnglish ? "Back to news" : "Nazad na novosti"}
                </Link>

                {loadError && (
                  <NewsFallbackNotice
                    message={loadError}
                    source={fallbackSource}
                    onRetry={() => window.location.reload()}
                  />
                )}

                <h1>{post.attributes.Title}</h1>

                <div className="meta-info mb-2">
                  <ul>
                    <li>
                      <i className="fas fa-calendar-alt" /> {post.attributes.Date || "FPEP"}
                    </li>
                  </ul>
                </div>

                {post.attributes.Image && (
                  <div className="fpep-news-detail-cover-wrap">
                    <ResilientImage
                      candidates={post.attributes.ImageCandidates?.length ? post.attributes.ImageCandidates : post.attributes.Image}
                      fallbackSrc={localFallbackImage}
                      alt={post.attributes.Title}
                      className="fpep-news-detail-cover"
                    />
                  </div>
                )}

                <div className="content textjustify cms-news-content">
                  {hasCmsContentHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: post.contentHtml }} />
                  ) : (
                    paragraphs.map((paragraph, idx) => <p key={idx}>{paragraph}</p>)
                  )}
                </div>

                {post.attributes.Gallery?.data?.length > 0 && (
                  <section className="fpep-news-gallery" aria-label={isEnglish ? "News gallery" : "Galerija uz novost"}>
                    <div className="fpep-news-gallery__header">
                      <div>
                        <span>{isEnglish ? "Gallery" : "Galerija"}</span>
                        <h2>{isEnglish ? "Photos from this news post" : "Fotografije iz ove objave"}</h2>
                      </div>
                      <strong>{post.attributes.Gallery.data.length} {isEnglish ? "photos" : "slika"}</strong>
                    </div>
                    <Slider {...sliderSettings}>
                      {post.attributes.Gallery.data.map((img, index) => (
                        <div key={index} className="fpep-news-gallery__slide">
                          <div className="fpep-news-gallery__frame">
                            <ResilientImage
                              candidates={img}
                              fallbackSrc=""
                              alt={isEnglish ? `gallery image ${index + 1}` : `galerija slika ${index + 1}`}
                              className="fpep-news-gallery__image"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </section>
                )}
              </article>
            </div>

            <div className="col-md-4 pt-10">
              <aside className="sidebar-widget">
                <section
                  id="custom_html-5"
                  className="widget_text widget widget_custom_html"
                >
                  <h2 className="widget-title">{t("sidebar.follow")}</h2>

                  <div className="textwidget custom-html-widget">
                    <div className="widget-social">
                      <a
                        href="https://www.facebook.com/fpebar.me"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fab fa-facebook-f" />
                      </a>

                      <a
                        href="https://www.instagram.com/fpepbar/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fab fa-instagram" />
                      </a>

                      <a
                        href="https://www.youtube.com/@fpepbar"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fab fa-youtube" />
                      </a>

                      <a
                        href="https://www.tiktok.com/@fpepbar"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Follow us on Tiktok"
                        title="Tiktok"
                      >
                        <i className="fab fa-tiktok" />
                      </a>

                      <a
                        href="https://www.linkedin.com/school/fpepbar/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Follow us on LinkedIn"
                        title="LinkedIn"
                      >
                        <i className="fab fa-linkedin-in" />
                      </a>
                    </div>
                  </div>
                </section>

                <section id="categories-1" className="widget widget_categories">
                  <h2 className="widget-title">{t("sidebar.useful")}</h2>
                  <ul>
                    <li className="cat-item cat-item-16">
                      <Link to="/o-fpep">{t("about.fpep")}</Link>
                    </li>
                    <li className="cat-item cat-item-23">
                      <Link to="/akreditacija-fpep">
                        {t("admission.Akreditacija")}
                      </Link>
                    </li>
                    <li className="cat-item cat-item-18">
                      <Link to="/upis">{t("upis.title")}</Link>
                    </li>
                    <li className="cat-item cat-item-22">
                      <Link to="/cijene-studija">
                        {t("admission.Skolarina")}
                      </Link>
                    </li>
                    <li className="cat-item cat-item-22">
                      <Link to="/erasmus">{t("saradnja.erasmus")}</Link>
                    </li>
                    <li className="cat-item cat-item-22">
                      <Link to="/projekti">{t("saradnja.projekti")}</Link>
                    </li>
                    <li className="cat-item cat-item-22">
                      <Link to="/dom-studenata">{t("studenti.dom")}</Link>
                    </li>
                    <li className="cat-item cat-item-22">
                      <Link to="/galerija">{t("navbar.Gallery")}</Link>
                    </li>
                    <li className="cat-item cat-item-22">
                      <Link to="/novosti">{t("navbar.News")}</Link>
                    </li>
                  </ul>
                </section>

                <section
                  id="recent-posts-4"
                  className="widget widget_recent_entries"
                >
                  <h2 className="widget-title">{t("sidebar.study")}</h2>
                  <ul>
                    <li>
                      <span className="post-date">
                        <strong>{t("programs.osnovne")}</strong> <br />
                      </span>
                      <Link to="/racunovodstvo-finansije-i-bankarstvo">
                        {t("osnovne.rfb")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <Link to="/menadzment">
                        {t("osnovne.menadzment")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <Link to="/pravo-osnovne">
                        {t("osnovne.pravo")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <span className="post-date">
                        <strong>{t("programs.master")}</strong> <br />
                      </span>
                      <Link to="/poslovna-ekonomija">
                        {t("master.poslovnaEkonomija")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <Link to="/ekonomija-javnog-sektora">
                        {t("master.ekonomijaJavnogSektora")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <Link to="/pravo-master">
                        {t("master.pravo")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <span className="post-date">
                        <strong>{t("programs.specijalisticke")}</strong> <br />
                      </span>
                      <Link to="/menadzment-javnog-sektora-specijalisticke">
                        {t("specijalisticke.mjs")}
                        <br />
                      </Link>
                    </li>

                    <li>
                      <Link to="/menadzment-profitnog-sektora-specijalisticke">
                        {t("specijalisticke.mps")}
                        <br />
                      </Link>
                    </li>
                  </ul>
                </section>

                <section
                  id="recent-posts"
                  className="widget widget_recent_entries"
                >
                  <h2 className="widget-title">{isEnglish ? "Latest posts" : "Najnovije objave"}</h2>
                  <ul>
                    {recentPosts.map((rPost) => (
                      <li key={`${rPost.source || "local"}-recent-${rPost.cmsId || rPost.id}`}>
                        <Link to={getNewsPath(rPost)}>
                          {rPost.attributes.Title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              </aside>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default NovostDetail;
